import React, { useState, useEffect } from "react";

import { Grid, MenuItem } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import Input from "../../../../../../../../components/Form/Input";
import RadioGroup from "../../../../../../../../components/Form/RadioGroup";
import Select from "../../../../../../../../components/Form/Select";
import Modal from "../../../../../../../../components/Generic/Modal";
import { Error } from "../../../../../../../../models/error";
import { TaskType } from "../../../../../../../../models/task.type";
import { Status, TaskTypeRole } from "../../../../../../../../util/enums";
import useStyles, { InputWrapper, SequenceInputWrapper } from "./styles";

interface Props {
  taskType?: TaskType;
  open: boolean;
  hasError?: boolean;
  responseError?: Error;
  isLoading?: boolean;
  maxSequence: number;
  closeModal: () => void;
  submit: (taskType: TaskType) => void;
}

const TaskTypeModal = ({
  taskType,
  open,
  hasError,
  responseError,
  isLoading,
  maxSequence = 1,
  closeModal,
  submit,
}: Props) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const [selectedTaskType, setSelectedTaskType] = useState<TaskType>();
  const [status, setStatus] = useState<string>(Status.INACTIVE);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);

  const getErrorByField = (field: string) => errors?.find((error) => error.path === field)?.message;

  const schema = yup.object().shape({
    name: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    role: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    sequence: yup
      .number()
      .typeError(translate("sequenceMustBeAValidNumber"))
      .max(1000, translate("sequenceMustBeSmallerThan1000"))
      .integer(translate("sequenceMustBeAIntegerNumber"))
      .positive(translate("sequenceMustBeGreaterThan0"))
      .required(translate("thisFieldIsRequired")),
  });

  const clearFields = () => {
    setSelectedTaskType(undefined);
    setStatus(Status.INACTIVE);
  };

  const handleValidateSuccess = (taskType?: TaskType): boolean => {
    schema.validateSync(
      {
        name: taskType?.name,
        role: taskType?.role,
        sequence: taskType?.sequence,
      },
      { abortEarly: false }
    );
    setErrors([]);

    return true;
  };

  const handleValidateError = (error: any): boolean => {
    if (error instanceof yup.ValidationError) setErrors(error?.inner);

    return false;
  };

  const isValid = (taskType?: TaskType) => {
    try {
      return handleValidateSuccess(taskType);
    } catch (error) {
      return handleValidateError(error);
    }
  };

  const handleSubmit = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    if (isValid(selectedTaskType)) {
      if (selectedTaskType)
        submit({
          ...selectedTaskType,
          status,
          milestoneEnabledDefault: !!selectedTaskType?.milestoneEnabledDefault,
        });
    }
  };

  useEffect(() => {
    if (taskType) {
      setSelectedTaskType(taskType);
      setStatus(taskType.status || "");
    } else {
      setSelectedTaskType({ sequence: maxSequence + 1 } as TaskType);
    }
  }, [taskType]);

  useEffect(() => {
    if (hasError !== undefined && !hasError) {
      clearFields();
      closeModal();
    }
  }, [hasError]);

  useEffect(() => {
    if (hasFormBeenSubmitted) {
      isValid(selectedTaskType);
    }
  }, [selectedTaskType]);

  return (
    <Modal
      data-cy="taskTypeModal"
      open={open}
      handleClose={closeModal}
      title={taskType ? translate("editTaskType") : translate("newTask")}
      showActionButtons
      actionButtonProps={{
        dataCyCancel: "cancelButton",
        onCancel: () => {
          clearFields();
          closeModal();
        },
        loading: isLoading,
        saveDisabled: isLoading,
        textSave: taskType ? translate("update") : translate("create"),
        dataCySave: "submitButton",
        onSave: handleSubmit,
      }}
      labelStatus={translate("status")}
      showStatusField={true}
      statusFieldProps={{
        label: translate("taskTypeStatus"),
        value: status,
        dataCy: "selectStatus",
        placeholder: translate("selectAnOption"),
        handleChange: (value) => setStatus(value),
      }}
    >
      <section className={classes.modalSection}>
        <Grid container direction="row" alignItems="flex-start" spacing={2}>
          <InputWrapper item>
            <Input
              disabled={isLoading}
              name="name"
              required
              label={translate("name")}
              placeholder={translate("name")}
              error={!!getErrorByField("name")}
              errorMessage={getErrorByField("name")}
              value={selectedTaskType?.name || ""}
              onChange={(event) =>
                setSelectedTaskType((state) =>
                  state
                    ? { ...state, name: event.target.value }
                    : ({ name: event.target.value } as TaskType)
                )
              }
            />
          </InputWrapper>
          <InputWrapper item>
            <Select
              name="typeField"
              data-cy={"select"}
              required
              displayEmpty
              disabled={isLoading}
              label={translate("taskType")}
              placeholder={translate("selectAnOption")}
              error={!!getErrorByField("role")}
              errorMessage={getErrorByField("role")}
              value={selectedTaskType?.role || ""}
              onChange={(event) =>
                setSelectedTaskType((state) =>
                  state
                    ? { ...state, role: event.target.value }
                    : ({ role: event.target.value } as TaskType)
                )
              }
            >
              <MenuItem value={TaskTypeRole.GENERAL}>{translate("general")}</MenuItem>
              <MenuItem value={TaskTypeRole.OPERATIONS}>{translate("operations")}</MenuItem>
            </Select>
          </InputWrapper>
          <SequenceInputWrapper item>
            <Input
              disabled={isLoading}
              name="sequence"
              type={"number"}
              label={translate("sequence")}
              placeholder={translate("sequence")}
              error={!!getErrorByField("sequence")}
              errorMessage={getErrorByField("sequence")}
              errorResponse={responseError}
              value={selectedTaskType?.sequence || ""}
              onChange={(event) =>
                setSelectedTaskType((state) =>
                  state
                    ? { ...state, sequence: Number(event.target.value) }
                    : ({ sequence: Number(event.target.value) } as TaskType)
                )
              }
            />
          </SequenceInputWrapper>
        </Grid>

        <Grid container direction="row" alignItems="flex-start" spacing={2}>
          <InputWrapper item>
            <RadioGroup
              label={translate("isItBookable")}
              value={!!selectedTaskType?.isBookable}
              disabled={isLoading}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setSelectedTaskType((state) =>
                  state
                    ? { ...state, isBookable: event?.target?.value === "true" }
                    : ({ isBookable: event?.target?.value === "true" } as TaskType)
                );
              }}
              options={[
                {
                  label: translate("yes"),
                  value: true,
                },
                {
                  label: translate("no"),
                  value: false,
                },
              ]}
            />
          </InputWrapper>

          <InputWrapper item>
            <RadioGroup
              label={translate("isItByDefaultAMilestone")}
              value={!!selectedTaskType?.milestoneEnabledDefault}
              disabled={isLoading}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setSelectedTaskType((state) =>
                  state
                    ? { ...state, milestoneEnabledDefault: event?.target?.value === "true" }
                    : ({ milestoneEnabledDefault: event?.target?.value === "true" } as TaskType)
                );
              }}
              options={[
                {
                  label: translate("yes"),
                  value: true,
                },
                {
                  label: translate("no"),
                  value: false,
                },
              ]}
            />
          </InputWrapper>
        </Grid>
      </section>
    </Modal>
  );
};

export default TaskTypeModal;
